import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomButtonGroup, ButtonGroupItem } from '@/components/CustomButtonGroup/CustomButtonGroup';
import { CustomTooltip } from '@/components/CustomTooltip/CustomTooltip';
import { BdsCheckbox, BdsIcon, BdsInput, BdsSwitch, BdsChipClickable } from '@/core/constants/webComponents';
import { useBdsChip } from '@/core/hooks/useBdsChip';
import { dispatchFilterEvent } from '../../GlobalFilterModal';
import './ContentSection.scss';

interface PublicationIDChipProps {
  id: string;
  onRemove: () => void;
}

const PublicationIDChip: React.FC<PublicationIDChipProps> = ({ id, onRemove }) => {
  const ref = useRef<HTMLElement | null>(null);
  useBdsChip(ref, onRemove);

  return (
    <span className="pub-id-selector__chip-wrapper">
      <BdsChipClickable
        ref={ref as any}
        color="outline"
        close
      >
        <span className="pub-id-selector__chip-content">
          <span className="pub-id-selector__chip-text">{id}</span>
        </span>
      </BdsChipClickable>
    </span>
  );
};

type TriState = 'add' | 'remove' | null;

export interface ConversationPublicationType {
  organic: boolean;
  darkPost: boolean;
}

export interface ContentData {
  textualSearch: string;
  imageTranscription: boolean;
  imageTranscriptionState?: TriState;
  collectedByAI: boolean;
  collectedByAIState?: TriState;
  publicationID: string;
  includeMentions: boolean;
  contentType: Set<string>;
  publicationType: ConversationPublicationType;
}

interface ContentSectionProps {
  initialValues?: ContentData;
  onChange?: (newValues: ContentData) => void;
  errorMessage?: string;
  onCountChange?: (count: number) => void;
  disableProprietaryChannelsFilters?: boolean;
  disableStilingueEyes?: boolean;
  disableIncludeMentions?: boolean;
  showPublicationType?: boolean;
}

export const contentTypes: ButtonGroupItem[] = [
  { id: 'text', icon: 'ab' },
  { id: 'image', icon: 'file-image' },
  { id: 'video', icon: 'video' },
  { id: 'audio', icon: 'speaker' },
];

export const ALL_CONTENT_TYPE_IDS: ReadonlySet<string> = new Set(contentTypes.map((t) => t.id));

export const defaultValues: ContentData = {
  textualSearch: '',
  imageTranscription: false,
  imageTranscriptionState: null,
  collectedByAI: false,
  collectedByAIState: null,
  publicationID: '',
  includeMentions: false,
  contentType: new Set(contentTypes.map((t) => t.id)),
  publicationType: { organic: false, darkPost: false },
};

const getTriState = (state: TriState | undefined, checked: boolean): TriState => {
  if (state === 'add' || state === 'remove') return state;
  return checked ? 'add' : null;
};

const toChecked = (state: TriState): boolean => state === 'add';

function useBdsInputEvent<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  onValue: (value: string) => void,
) {
  const callbackRef = useRef(onValue);

  useEffect(() => {
    callbackRef.current = onValue;
  }, [onValue]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const value = (event.target as HTMLInputElement)?.value ?? customEvent.detail?.value ?? '';
      callbackRef.current(value);
    };
    element.addEventListener('bdsInput', handler);
    return () => element.removeEventListener('bdsInput', handler);
  }, [ref]);
}

function useBdsChangeEvent<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  onValue: (checked: boolean) => void,
) {
  const callbackRef = useRef(onValue);

  useEffect(() => {
    callbackRef.current = onValue;
  }, [onValue]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const handler = (event: Event) => {
      const checked = (event as CustomEvent).detail?.checked ?? false;
      callbackRef.current(checked);
    };
    element.addEventListener('bdsChange', handler);
    return () => element.removeEventListener('bdsChange', handler);
  }, [ref]);
}

const getNextTriState = (current: TriState): TriState => {
  if (current === null) return 'add';
  if (current === 'add') return 'remove';
  return null;
};

export const ContentSection: React.FC<ContentSectionProps> = ({
  initialValues = defaultValues,
  onChange,
  errorMessage = '',
  onCountChange,
  disableProprietaryChannelsFilters = false,
  disableStilingueEyes = false,
  disableIncludeMentions = false,
  showPublicationType = false,
}) => {
  const { t } = useTranslation();

  const [data, setData] = useState<ContentData>(initialValues);
  const dataRef = useRef<ContentData>(data);

  const imageTranscriptionState = getTriState(data.imageTranscriptionState, data.imageTranscription);
  const collectedByAIState = getTriState(data.collectedByAIState, data.collectedByAI);
  const isImageTranscriptionDisabled = disableProprietaryChannelsFilters || disableStilingueEyes;
  const isCollectedByAIDisabled = disableProprietaryChannelsFilters || disableStilingueEyes || imageTranscriptionState === 'remove';

  useEffect(() => {
    if (!onCountChange) return;
    
    const count = [
      data.textualSearch.length > 0 ? 1 : 0,
      imageTranscriptionState == null ? 0 : 1,
      collectedByAIState == null ? 0 : 1,
      data.publicationID.length > 0 ? 1 : 0,
      data.includeMentions ? 1 : 0,
      data.contentType.size === ALL_CONTENT_TYPE_IDS.size ? 0 : 1,
      (showPublicationType && (data.publicationType.darkPost !== data.publicationType.organic)) ? 1 : 0,
    ].reduce((sum, val) => sum + val, 0);

    onCountChange(count);
  }, [data, onCountChange, imageTranscriptionState, collectedByAIState, showPublicationType]);

  const update = useCallback(
    <K extends keyof ContentData>(key: K, value: ContentData[K]) => {
      const next = { ...dataRef.current, [key]: value };
      dataRef.current = next;
      setData(next);
      onChange?.(next);
      dispatchFilterEvent('CONTENT', String(key).toUpperCase(), value);
    },
    [onChange],
  );

  const [typedValue, setTypedValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const textualSearchRef = useRef<HTMLBdsInputElement | null>(null);
  const incMentionsRef = useRef<HTMLBdsSwitchElement | null>(null);
  const organicRef = useRef<HTMLElement | null>(null);
  const darkPostRef = useRef<HTMLElement | null>(null);

  useBdsInputEvent(textualSearchRef, useCallback((value) => update('textualSearch', value), [update]));

  const processTypedValue = (value: string) => {
    const rawText = value.trim();
    if (!rawText) return;

    const newItems = rawText
      .split(/[,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (newItems.length > 0) {
      const currentItems = data.publicationID
        ? data.publicationID.split(',').map((id) => id.trim()).filter(Boolean)
        : [];

      const combinedItems = [...currentItems];
      newItems.forEach((item) => {
        if (!combinedItems.includes(item)) {
          combinedItems.push(item);
        }
      });

      update('publicationID', combinedItems.join(', '));
    }
    setTypedValue('');
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processTypedValue(typedValue);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    processTypedValue(typedValue);
  };

  const handleRemoveChip = (indexToRemove: number) => {
    const currentItems = data.publicationID
      ? data.publicationID.split(',').map((id) => id.trim()).filter(Boolean)
      : [];

    const updatedItems = currentItems.filter((_, idx) => idx !== indexToRemove);
    update('publicationID', updatedItems.join(', '));
  };

  const publicationIDs = useMemo(() => {
    return data.publicationID
      ? data.publicationID.split(',').map((id) => id.trim()).filter(Boolean)
      : [];
  }, [data.publicationID]);

  const handleImageTranscriptionToggle = useCallback(() => {
    if (isImageTranscriptionDisabled) return;
    const current = getTriState(dataRef.current.imageTranscriptionState, dataRef.current.imageTranscription);
    const nextState = getNextTriState(current);

    const next: ContentData = {
      ...dataRef.current,
      imageTranscription: toChecked(nextState),
      imageTranscriptionState: nextState,
    };

    if (nextState === 'remove') {
      next.collectedByAI = false;
      next.collectedByAIState = null;
    }

    dataRef.current = next;
    setData(next);
    onChange?.(next);
    dispatchFilterEvent('CONTENT', 'IMAGE_TRANSCRIPTION', next.imageTranscription);
    dispatchFilterEvent('CONTENT', 'IMAGE_TRANSCRIPTION_STATE', next.imageTranscriptionState);
    if (nextState === 'remove') {
      dispatchFilterEvent('CONTENT', 'COLLECTED_BY_AI', false);
      dispatchFilterEvent('CONTENT', 'COLLECTED_BY_AI_STATE', null);
    }
  }, [isImageTranscriptionDisabled, onChange]);

  const handleCollectedByAIToggle = useCallback(() => {
    const firstState = getTriState(dataRef.current.imageTranscriptionState, dataRef.current.imageTranscription);
    if (disableProprietaryChannelsFilters || disableStilingueEyes || firstState === 'remove') return;

    const current = getTriState(dataRef.current.collectedByAIState, dataRef.current.collectedByAI);
    const nextState = getNextTriState(current);

    const next: ContentData = {
      ...dataRef.current,
      collectedByAI: toChecked(nextState),
      collectedByAIState: nextState,
    };

    dataRef.current = next;
    setData(next);
    onChange?.(next);
    dispatchFilterEvent('CONTENT', 'COLLECTED_BY_AI', next.collectedByAI);
    dispatchFilterEvent('CONTENT', 'COLLECTED_BY_AI_STATE', next.collectedByAIState);
  }, [disableProprietaryChannelsFilters, disableStilingueEyes, onChange]);

  useBdsChangeEvent(incMentionsRef, useCallback((checked) => {
    if (disableProprietaryChannelsFilters || disableIncludeMentions) return;
    update('includeMentions', checked);
  }, [disableProprietaryChannelsFilters, disableIncludeMentions, update]));

  useBdsChangeEvent(organicRef, useCallback((checked) => {
    const next = { ...dataRef.current.publicationType, organic: checked };
    update('publicationType', next);
  }, [update]));

  useBdsChangeEvent(darkPostRef, useCallback((checked) => {
    const next = { ...dataRef.current.publicationType, darkPost: checked };
    update('publicationType', next);
  }, [update]));

  const toggleContentType = useCallback(
    (type: string) => {
      const next = new Set(dataRef.current.contentType);
      if (!next.delete(type)) next.add(type);
      const updated = { ...dataRef.current, contentType: next };
      dataRef.current = updated;
      setData(updated);
      onChange?.(updated);
      dispatchFilterEvent('CONTENT', `CONTENT_TYPE_${type.toUpperCase()}`, next);
    },
    [onChange],
  );

  return (
    <div className="content-section">
      <div className="content-section__group">
        <div className="content-section__label-wrapper">
          <span className="content-section__label">{t('globalFilter.textualSearch')}</span>
          <CustomTooltip content={t('globalFilter.textualSearchTooltip')} position="top" maxWidth="260px">
            <BdsIcon name="info" size="x-small" />
          </CustomTooltip>
        </div>
        <BdsInput
          ref={textualSearchRef}
          placeholder={t('globalFilter.textualSearchPlaceholder')}
          value={data.textualSearch}
          danger={!!errorMessage}
          error-message={errorMessage}
        />
      </div>

      <div className={`content-section__group${(disableProprietaryChannelsFilters || disableStilingueEyes) ? ' content-section__group--disabled' : ''}`}>
        <div className="content-section__label-wrapper">
          <span className="content-section__label">{t('globalFilter.stilingueEyes')}</span>
          <CustomTooltip content={t('globalFilter.stilingueEyesTooltip')} position="top" maxWidth="260px">
            <BdsIcon name="info" size="x-small" />
          </CustomTooltip>
        </div>
        <div className="content-section__checkboxes">
          <div className={`content-section__checkbox-item${imageTranscriptionState === 'remove' ? ' content-section__checkbox-item--negated' : ''}`}>
            <button
              type="button"
              className="content-section__tri-option"
              onClick={handleImageTranscriptionToggle}
              disabled={isImageTranscriptionDisabled}
              data-testid="image-transcription-option"
            >
              <span
                className={`content-section__tri-checkbox content-section__tri-checkbox--${imageTranscriptionState ?? 'empty'}`}
                aria-hidden="true"
              >
                {imageTranscriptionState === 'add' && <span className="content-section__tri-icon">✓</span>}
                {imageTranscriptionState === 'remove' && <span className="content-section__tri-icon">−</span>}
              </span>
              <span className="content-section__tri-label">{t('globalFilter.imageTranscription')}</span>
            </button>
            <CustomTooltip content={t('globalFilter.imageTranscriptionTooltip')} position="top" maxWidth="260px">
              <BdsIcon name="info" size="x-small" />
            </CustomTooltip>
          </div>
          <div className={`content-section__checkbox-item${collectedByAIState === 'remove' ? ' content-section__checkbox-item--negated' : ''}`}>
            <button
              type="button"
              className="content-section__tri-option"
              onClick={handleCollectedByAIToggle}
              disabled={isCollectedByAIDisabled}
              data-testid="collected-by-ai-option"
            >
              <span
                className={`content-section__tri-checkbox content-section__tri-checkbox--${collectedByAIState ?? 'empty'}`}
                aria-hidden="true"
              >
                {collectedByAIState === 'add' && <span className="content-section__tri-icon">✓</span>}
                {collectedByAIState === 'remove' && <span className="content-section__tri-icon">−</span>}
              </span>
              <span className="content-section__tri-label">{t('globalFilter.collectedByAI')}</span>
            </button>
            <CustomTooltip content={t('globalFilter.collectedByAITooltip')} position="top" maxWidth="260px">
              <BdsIcon name="info" size="x-small" />
            </CustomTooltip>
          </div>
        </div>
      </div>

      <div className="content-section__group">
        <div className="content-section__label-wrapper">
          <span className="content-section__label">{t('globalFilter.publicationID')}</span>
          <CustomTooltip content={t('globalFilter.publicationIDTooltip')} position="top" maxWidth="260px">
            <BdsIcon name="info" size="x-small" />
          </CustomTooltip>
        </div>
        <label 
          className={`pub-id-selector__field${isFocused ? ' pub-id-selector__field--focused' : ''}`}
        >
          <div className="pub-id-selector__chips">
            {publicationIDs.map((id, index) => (
              <PublicationIDChip
                key={`${id}-${index}`}
                id={id}
                onRemove={() => handleRemoveChip(index)}
              />
            ))}
            <input
              ref={inputRef}
              type="text"
              className="pub-id-selector__input"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={handleInputBlur}
              placeholder={publicationIDs.length === 0 ? t('globalFilter.publicationIDPlaceholder') : ''}
            />
          </div>
        </label>
      </div>

      <div className={`content-section__group${(disableProprietaryChannelsFilters || disableIncludeMentions) ? ' content-section__group--disabled' : ''}`}>
        <div className="content-section__switch-wrapper">
          <BdsSwitch
            ref={incMentionsRef}
            name="include-mentions"
            refer="include-mentions"
            checked={data.includeMentions}
            size="short"
            disabled={disableProprietaryChannelsFilters || disableIncludeMentions}
          />
          <span className="content-section__switch-label">{t('globalFilter.includeMentions')}</span>
          <CustomTooltip content={t('globalFilter.includeMentionsTooltip')} position="top">
            <BdsIcon name="info" size="x-small" />
          </CustomTooltip>
        </div>
      </div>

      {showPublicationType && (
        <div className="content-section__group">
          <span className="content-section__label">{t('globalFilter.conversationPublicationType')}</span>
          <div className="content-section__checkboxes">
            <BdsCheckbox
              ref={organicRef as any}
              label={t('globalFilter.conversationOrganic')}
              checked={data.publicationType.organic}
            />
            <BdsCheckbox
              ref={darkPostRef as any}
              label={t('globalFilter.conversationDarkPost')}
              checked={data.publicationType.darkPost}
            />
          </div>
        </div>
      )}

      <div className="content-section__group">
        <div className="content-section__type-wrapper">
          <span className="content-section__label">{t('globalFilter.type')}</span>
          <CustomButtonGroup
            options={
              contentTypes.map((type) => ({
                ...type,
                tooltipText: t(`globalFilter.contentTypes.${type.id}`),
              }))
            }
            selectedItems={data.contentType}
            onToggle={toggleContentType}
          />
        </div>
      </div>
    </div>
  );
};